import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import { $pegApi } from '@/boot/peg-api'
import { $logger } from '@/boot/logger'
import { $rollbar } from '@/boot/rollbar'
import { RETURN_STATUS, REFUND_STATUS, FINANCIAL_STATUS, CREDIT_TYPE } from '@/constants/userReturns.const'

export const useUserReturnsStore = defineStore('userReturns', () => {
  const route = useRoute()
  const queryClient = useQueryClient()

  // ===== QUERIES =====

  /**
   * Query hook for all user returns with pagination
   * @param {Ref} params - Query parameters ref
   * @returns {QueryResult}
   */
  function useAllUserReturnsQuery(params) {
    return useQuery({
      queryKey: ['userReturns', 'allReturns', params],
      queryFn: () => {
        return $pegApi.Users.Returns.getBulk(params?.value)
      },
    })
  }

  /**
   * Query hook for single user return details
   * @param {Ref} returnItemId - Return ID ref
   * @returns {QueryResult}
   */
  function useSingleUserReturnQuery(returnItemId) {
    return useQuery({
      queryKey: ['userReturns', 'singleReturn', returnItemId],
      queryFn: () => $pegApi.Users.Returns.getSingle(returnItemId.value),
      enabled: computed(() => !!returnItemId.value),
      placeholderData: () => {
        // Pre-seed from list cache for instant page load
        const listQueries = queryClient.getQueriesData({ queryKey: ['userReturns', 'allReturns'] })
        for (const [, listData] of listQueries) {
          const returnObj = listData?.returns?.find(r => r.returnItems?.some(item => item.id === returnItemId.value))
          if (returnObj) return { return: returnObj }
        }
      },
    })
  }

  // ===== RETURN ITEM LOGIC (for detail pages) =====

  // returnItemId must be reactive to route changes
  const returnItemId = computed(() => route.params?.returnItemId || null)

  // Query data from single return query
  const singleReturnQueryResult = useSingleUserReturnQuery(returnItemId)

  const data = computed(() => singleReturnQueryResult?.data?.value)

  // Core data
  const returnData = computed(() => data.value?.return || {})
  const orderData = computed(() => returnData.value.orderData || {})
  const returnItem = computed(() => {
    return returnData.value.returnItems?.find(item => item.id === returnItemId.value) || {}
  })
  const retailerData = computed(() => returnData.value.retailerData || {})
  const returnStatus = computed(() => returnItem.value.returnStatus || '')

  const isLoading = computed(() => {
    // If we have matching data (real or placeholder), no loading needed
    if (returnItem.value?.id) return false
    // Show loading while query is pending or fetching without data
    return Boolean(singleReturnQueryResult?.isPending?.value || singleReturnQueryResult?.isFetching?.value)
  })

  // Return status checks
  const isSubmitted = computed(() => returnStatus.value === RETURN_STATUS.IN_PROGRESS)
  const isReadyToShip = computed(() => returnStatus.value === RETURN_STATUS.READY_TO_SHIP)
  const isShipped = computed(() => returnStatus.value === RETURN_STATUS.IN_TRANSIT)
  const isHandedOff = computed(() => returnStatus.value === RETURN_STATUS.HANDED_OFF)
  const isComplete = computed(() => returnStatus.value === RETURN_STATUS.COMPLETE)
  const isGiftReturn = computed(() => Boolean(returnItem.value?.isGiftReturn))
  function checkTerminalStatus(returnStatus) {
    return [RETURN_STATUS.COMPLETE, RETURN_STATUS.HANDED_OFF].includes(returnStatus)
  }
  const isTerminal = computed(() => checkTerminalStatus(returnStatus.value))

  // Credit and refund calculations
  /**
   * @description Calculate available store credit for refund
   * @param {Object} returnItem
   * @param {Object} retailerData
   * @returns {Number} available credit amount
   */
  function calculateAvailableCredit(returnItem, retailerData) {
    if (checkRefundAccepted(returnItem)) {
      return 0
    }
    return Math.min(returnItem?.returnAmount, retailerData.storeCredit?.balance) || 0
  }
  const availableCredit = computed(() => calculateAvailableCredit(returnItem.value, retailerData.value))

  const eligibleRefundAmount = computed(() => {
    if (refundIsAccepted.value) return returnItem.value.refundAmount
    if (creditType.value === CREDIT_TYPE.REFUND) return returnItem.value.refundAmount || returnItem.value.returnAmount || 0
    return availableCredit.value
  })

  // Shipping cost deduction
  const shippingCostDeduction = computed(() => returnItem.value.shippingCostDeduction || null)
  const hasShippingDeduction = computed(() => Boolean(shippingCostDeduction.value?.shouldDeduct))
  const isEstimateDeduction = computed(() => Boolean(shippingCostDeduction.value?.isEstimate))

  /**
   * The net return amount after any shipping cost deduction (not capped by eligibility).
   * Components should use this for display only when they don't need eligibility capping.
   */
  const netReturnAmount = computed(() => {
    if (hasShippingDeduction.value) return shippingCostDeduction.value.netAmount || 0
    return returnItem.value.returnAmount || 0
  })

  /**
   * The display amount for refund/credit, capped to the eligible refund amount.
   * Ensures the UI never shows more than the customer can actually receive.
   */
  const displayAmount = computed(() => {
    return Math.min(eligibleRefundAmount.value || 0, netReturnAmount.value)
  })

  // Refund status checks
  /**
   * @description Check if item was refunded directly by the retailer in Shopify
   * @param {Object} returnItem
   * @returns {Boolean}
   */
  function checkRefundedByRetailer(returnItem) {
    return Boolean(returnItem?.refundedByRetailer || returnItem?.financialStatus === FINANCIAL_STATUS.REFUNDED_BY_RETAILER)
  }
  const refundedByRetailer = computed(() => checkRefundedByRetailer(returnItem.value))
  const retailerRefundAmount = computed(() => returnItem.value?.retailerRefundAmount || 0)

  /**
   * @description Check if refund has been accepted or processed
   * @param {Object} returnItem
   * @returns {Boolean}
   */
  function checkRefundAccepted(returnItem) {
    return returnItem?.refundStatus === REFUND_STATUS.ACCEPTED || returnItem?.financialStatus === FINANCIAL_STATUS.REFUNDED
  }
  /**
   * @description Check if refund is available to be accepted
   * @param {Object} returnItem
   * @param {Number} availableCredit - Available store credit amount
   * @returns {Boolean}
   */
  function checkRefundAvailable(returnItem, availableCredit) {
    return (
      returnItem?.refundStatus === REFUND_STATUS.ELIGIBLE &&
      availableCredit > 0 &&
      !checkRefundAccepted(returnItem) &&
      !checkRefundedByRetailer(returnItem) &&
      !returnItem?.refundIsExpired &&
      !returnItem?.isGiftReturn
    )
  }
  const refundNotEligible = computed(() => {
    return returnItem.value.refundStatus === REFUND_STATUS.NOT_ELIGIBLE
  })

  const refundIsAvailable = computed(() => checkRefundAvailable(returnItem.value, availableCredit.value))

  const refundIsAccepted = computed(() => checkRefundAccepted(returnItem.value))

  // Refund expiration (server-computed fields)
  const refundIsExpired = computed(() => Boolean(returnItem.value.refundIsExpired))
  const refundExpirationDate = computed(() => returnItem.value.refundExpirationDate || null)
  const refundDaysRemaining = computed(() => returnItem.value.refundDaysRemaining ?? null)
  const refundExpirationUrgent = computed(
    () => refundDaysRemaining.value !== null && refundDaysRemaining.value <= 7 && refundDaysRemaining.value > 0
  )

  // Credit type and financial status (new API fields)
  // Derive creditType for legacy returns that lack the field:
  // - If refund was accepted/processed or retailer refunded directly → REFUND
  // - Otherwise default to IN_STORE_CREDIT (most common outcome)
  const creditType = computed(() => {
    if (returnItem.value.creditType) return returnItem.value.creditType
    // Legacy fallback: only treat as REFUND when refund was actually accepted/processed
    // or retailer refunded directly. ELIGIBLE means the refund *option* is available,
    // but the default outcome is still in-store credit.
    if (checkRefundAccepted(returnItem.value) || checkRefundedByRetailer(returnItem.value)) {
      return CREDIT_TYPE.REFUND
    }
    return CREDIT_TYPE.IN_STORE_CREDIT
  })
  const financialStatus = computed(() => returnItem.value.financialStatus || null)

  // Legacy fallback: if financialStatus is missing but a store credit balance exists,
  // treat credit as created so the UI doesn't show "pending" for already-issued credit.
  // NOTE: commenting out the legacy store credit check for now - we do not want to show "store credit created" when the return is for a refund
  // return_items.credit_type determines what the customer is getting
  // const hasLegacyStoreCredit = computed(() => {
  //   const storeCredit = retailerData.value?.storeCredit
  //   const credit = Number(storeCredit?.availableCredit)
  //   const balance = Number(storeCredit?.balance)
  //   return (Number.isFinite(credit) && credit > 0) || (Number.isFinite(balance) && balance > 0)
  // })

  const storeCreditCreated = computed(
    () => financialStatus.value === FINANCIAL_STATUS.IN_STORE_CREDIT_CREATED // || hasLegacyStoreCredit.value
  )
  const refundProcessed = computed(() => financialStatus.value === FINANCIAL_STATUS.REFUNDED)

  // Card visibility logic
  // Show store credit card when: creditType is IN_STORE_CREDIT, or store credit has been created.
  // Not shown in handed-off state.
  const showStoreCreditCard = computed(() => {
    if (isHandedOff.value) return false
    if (storeCreditCreated.value) return true
    if (creditType.value === CREDIT_TYPE.IN_STORE_CREDIT) return true
    return false
  })

  // Show refund card when: creditType is REFUND, or refund has been processed,
  // or retailer refunded directly.
  // Never show for gift returns (gift returns only qualify for in-store credit)
  // or when the refund offer has expired.
  const showRefundCard = computed(() => {
    if (isGiftReturn.value) return false
    if (refundIsExpired.value) return false
    if (isHandedOff.value && !refundedByRetailer.value) return false
    if (refundedByRetailer.value) return true
    if (refundProcessed.value) return true
    if (creditType.value === CREDIT_TYPE.REFUND) return true
    return false
  })

  // ===== MUTATIONS =====

  /**
   * Mutation hook for accepting a refund
   * @returns {MutationResult}
   */
  function useAcceptRefundMutation() {
    return useMutation({
      mutationFn: async returnItemId => {
        return $pegApi.Users.Returns.acceptRefund(returnItemId)
      },
      onSuccess: (_data, returnItemId) => {
        // Invalidate queries to refresh data
        // Include returnItemId to invalidate only this specific return's query
        queryClient.invalidateQueries({ queryKey: ['userReturns', 'singleReturn', returnItemId] })
        queryClient.invalidateQueries({ queryKey: ['userReturns', 'allReturns'] })
      },
      onError: error => {
        $rollbar.error('Error processing refund:', error)
      },
    })
  }

  /**
   * Mutation hook for creating a shipping label
   * @returns {MutationResult}
   */
  function useCreateShippingLabelMutation() {
    return useMutation({
      mutationFn: async returnItemId => {
        return $pegApi.Users.Returns.createShippingLabel(returnItemId)
      },
      onSuccess: (_data, returnItemId) => {
        // Invalidate queries to refresh data with new shipping label info
        queryClient.invalidateQueries({ queryKey: ['userReturns', 'singleReturn', returnItemId] })
        queryClient.invalidateQueries({ queryKey: ['userReturns', 'allReturns'] })
      },
      onError: error => {
        $rollbar.error('Error creating shipping label:', error)
      },
    })
  }

  /**
   * Mutation hook for emailing shipping labels
   * @returns {MutationResult}
   */
  function useEmailShippingLabelMutation() {
    return useMutation({
      mutationFn: async ({ returnItemId }) => {
        return $pegApi.Users.Returns.emailShippingLabel(returnItemId)
      },
      onError: error => {
        $rollbar.error('Error emailing shipping label:', error)
      },
    })
  }

  /**
   * Reset user returns state and clear query cache (called on logout)
   */
  function resetState() {
    // Invalidate ALL userReturns queries to prevent data leakage
    queryClient.removeQueries({ queryKey: ['userReturns'] })

    $logger.debug('UserReturnsStore: Query cache cleared on logout')
  }

  return {
    // Queries (for reuse anywhere)
    useAllUserReturnsQuery,
    useSingleUserReturnQuery,

    // Return item state (for detail pages)
    returnItemId,

    // Query state
    isLoading,

    // Computed data
    returnData,
    orderData,
    returnItem,
    retailerData,

    // Status
    isSubmitted,
    isReadyToShip,
    isShipped,
    isHandedOff,
    isComplete,
    isTerminal,
    isGiftReturn,
    checkTerminalStatus,

    // Credit and refund
    creditType,
    financialStatus,
    storeCreditCreated,
    refundProcessed,
    showStoreCreditCard,
    showRefundCard,
    eligibleRefundAmount,
    availableCredit,
    refundIsAvailable,
    refundNotEligible,
    refundIsAccepted,
    refundIsExpired,
    refundExpirationDate,
    refundDaysRemaining,
    refundExpirationUrgent,
    checkRefundAccepted,
    checkRefundAvailable,
    calculateAvailableCredit,

    // Shipping cost deduction
    shippingCostDeduction,
    hasShippingDeduction,
    isEstimateDeduction,
    displayAmount,

    // Direct retailer refund
    refundedByRetailer,
    retailerRefundAmount,
    checkRefundedByRetailer,

    // Mutations
    useAcceptRefundMutation,
    useCreateShippingLabelMutation,
    useEmailShippingLabelMutation,

    // State management
    resetState,
  }
})
